import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AchievementEntry } from '../mockAchievements';

interface AchievementTileProps {
  entry: AchievementEntry;
}

const AchievementTile: React.FC<AchievementTileProps> = ({ entry }) => {
  const pct = Math.min(100, Math.round((entry.current / entry.threshold) * 100));

  return (
    <Box
      sx={(theme) => ({
        alignItems: 'flex-start',
        bgcolor: 'background.paper',
        border: `1px solid ${
          entry.unlocked
            ? theme.palette.primary.main
            : theme.palette.mode === 'dark'
            ? '#303A48'
            : theme.palette.primary.tableBorder
        }`,
        borderRadius: 3,
        display: 'flex',
        gap: 2,
        height: '100%',
        opacity: entry.unlocked ? 1 : 0.92,
        p: 2,
        position: 'relative',
        transition: 'transform 80ms ease, border-color 80ms ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-1px)',
        },
      })}
    >
      <Medal unlocked={entry.unlocked} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'flex-start'}
          spacing={1}
        >
          <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {entry.title}
          </Typography>
          {entry.unlocked ? (
            <CheckCircleIcon sx={{ color: 'primary.aqua', fontSize: 18 }} />
          ) : (
            <LockOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          )}
        </Stack>
        <Typography
          variant={'body2'}
          color={'text.secondary'}
          sx={{ mt: 0.5 }}
        >
          {entry.description}
        </Typography>

        {entry.unlocked ? (
          <Typography
            variant={'caption'}
            sx={{ color: 'primary.aqua', fontWeight: 700, mt: 1.25, display: 'block' }}
          >
            {entry.unlockedAt
              ? `Отримано · ${entry.unlockedAt}`
              : 'Отримано'}
          </Typography>
        ) : (
          <Box sx={{ mt: 1.25 }}>
            <LinearProgress
              value={pct}
              variant={'determinate'}
              sx={{
                bgcolor: 'primary.vocabularyUnlearned',
                borderRadius: 10,
                height: 6,
                '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
              }}
            />
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              sx={{ mt: 0.5 }}
            >
              <Typography variant={'caption'} color={'text.secondary'}>
                {`${entry.current} / ${entry.threshold}${
                  entry.unit ? ` ${entry.unit}` : ''
                }`}
              </Typography>
              <Typography
                variant={'caption'}
                sx={{ color: 'primary.main', fontWeight: 700 }}
              >
                {`${pct}%`}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Medal: React.FC<{ unlocked: boolean }> = ({ unlocked }) => (
  <Box
    sx={(theme) => ({
      alignItems: 'center',
      bgcolor: unlocked
        ? 'primary.purple'
        : theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(0,0,0,0.04)',
      borderRadius: '50%',
      color: unlocked ? 'primary.main' : 'text.disabled',
      display: 'flex',
      flexShrink: 0,
      height: 48,
      justifyContent: 'center',
      width: 48,
    })}
  >
    <MilitaryTechIcon sx={{ fontSize: 26 }} />
  </Box>
);

export default AchievementTile;
